import { Button } from '@heroui/react';
import { ClipboardPaste, X } from 'lucide-react';
import { useMaterialClipboard } from '@/stores/materialClipboardStore';

interface MaterialClipboardBarProps {
  /** Panels the paste would touch, across both lists. */
  panelCount: number;
  onPaste: (materialId: string) => void;
}

/**
 * Appears under the Framing Configuration heading once a material is copied.
 * One paste fills both panel lists, so the action cannot live in a single
 * list header — the live count makes the blast radius visible before clicking.
 */
export default function MaterialClipboardBar({ panelCount, onPaste }: MaterialClipboardBarProps) {
  const materialId = useMaterialClipboard((s) => s.materialId);
  const materialName = useMaterialClipboard((s) => s.materialName);
  const clear = useMaterialClipboard((s) => s.clear);

  if (!materialId) return null;

  return (
    <div
      className="flex items-center gap-2 rounded-md px-2 py-1"
      style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border)' }}
    >
      <span className="text-[11px] truncate" style={{ color: 'var(--text-secondary)' }}>
        <span style={{ color: '#e67e22' }}>{materialName}</span> copied
      </span>
      <Button
        size="sm"
        variant="flat"
        className="h-6 min-w-0 px-2"
        startContent={<ClipboardPaste size={12} />}
        isDisabled={panelCount === 0}
        onPress={() => onPaste(materialId)}
      >
        <span className="text-[11px]">
          Paste to {panelCount} panel{panelCount === 1 ? '' : 's'}
        </span>
      </Button>
      <Button
        isIconOnly
        size="sm"
        variant="light"
        className="h-6 w-6 min-w-6 ml-auto"
        aria-label="Clear copied material"
        onPress={clear}
      >
        <X size={12} style={{ color: 'var(--text-muted)' }} />
      </Button>
    </div>
  );
}
